import { useEffect, useRef, useState } from "react";

/**
 * TgEmoji — емодзі однаково на ВСІХ пристроях (юзер 2026-06-15: на Android
 * системні емодзі — жах + лагали + вантажились із затримкою).
 *
 * Рендер:
 *  1. Статичний Apple-PNG показуємо ОДРАЗУ (миттєво, без порожнечі).
 *  2. Якщо для емодзі є Lottie (офіційний Telegram-набір, 60fps, лежить
 *     локально у /emoji/<cp>.json) — підвантажуємо й накладаємо зверху,
 *     PNG ховаємо. Анімація як у Telegram-чаті.
 *  3. Якщо PNG не завантажився — нативний символ.
 */

// Кодпоінти, для яких є локальний Lottie (Telegram AnimatedEmojies → JSON).
const LOTTIE_CPS = new Set([
  "1f399", "1f3a4", "1f3ac", "1f440", "1f447", "1f465", "1f48e", "1f4da",
  "1f4dd", "1f4b0", "1f4b8", "1f510", "1f511", "1f614", "1f680", "1f916",
  "1f9e1", "23f3", "26a1", "2705", "1f389", "1f973", "1f4ac", "1f91d",
  "1f4e8",
  // Юзер 2026-06-25/27: докачано з Telegram AnimatedEmojies
  "1f947", // 🥇 золота медаль (попап «Квест виконано» — 🏆 майже не анімувався)
  "274c",  // ❌ хрестик (попап «Це не вакансія»)
  // Owner 2026-07-23: 💻 для кроку «Формат вакансій» (⚡ не за темою).
  // Джерело — Google Noto animated (Telegram-набір лаптоп не анімує).
  "1f4bb", // 💻 ноутбук (формат роботи: онлайн/офлайн/гібрид)
  "1f382", // 🎂 торт (попап «День народження», owner 2026-07-23, Noto animated)
]);

// Юзер 2026-06-15: «де нема анімованого — НЕ статику, а найближчий за
// сенсом анімований». Підміна кодпоінта на доступний анімований аналог.
const ALIAS: Record<string, string> = {
  "1f512": "1f510", // 🔒 → 🔐 (замок)
  "1f513": "1f510", // 🔓 → 🔐
  "1f9e0": "1f916", // 🧠 → 🤖 (AI / Intelligence)
  // 🎁 → 💎. Пробував підкласти Noto-подарунок (2026-08-11) — власник одразу
  // впізнав андроїд-стиль і забракував. У Telegram-наборі AnimatedEmojies
  // подарунка НЕМА взагалі (перевірено через getStickerSet: 599 емодзі, самі
  // обличчя й серця), тож беремо найближче анімоване зі свого набору.
  "1f381": "1f48e", // 🎁 → 💎 (цінність/подарунок)
  "1f464": "1f465", // 👤 → 👥 (люди)
  // 🔖 → 📝. Owner 2026-09-10 побачив статичну закладку над заголовком попапа
  // «Збережені вакансії». Анімованої закладки взяти нема звідки: у
  // Telegram-наборі AnimatedEmojies її немає, а Noto власник уже забракував
  // (впізнає андроїд-стиль). Тому найближче анімоване зі свого набору —
  // блокнот: сенс той самий, «відкладене на потім».
  "1f516": "1f4dd", // 🔖 → 📝 (відкладене)
  "26a0": "23f3",   // ⚠️ → ⏳ (увага/час)
  // Owner 2026-09-13: у попапі «Підтверди відмову» стояла СТАТИКА 🥲.
  // Анімованого «зі слізьми» в наборі нема, найближче за сенсом — 😔.
  "1f972": "1f614", // 🥲 → 😔 (сум)
  // 📎 у кроці інструкції «Файли від замовника» теж була статикою;
  // скріпки в наборі нема, документи передає 📚.
  "1f4ce": "1f4da", // 📎 → 📚 (документи)
};

// Локальні Apple-PNG плейсхолдери (миттєво, same-origin) — щоб НЕ було
// білої порожнечі поки вантажиться Lottie (юзер 2026-06-15: «іконка
// з'являється через секунду, до того порожньо»). Раніше брали з CDN → лаг.
const PNG_BASE = "/emoji-png/";

// Cache-busting (owner 2026-07-23): емодзі-ассети (Lottie/PNG) віддаються по
// стабільному шляху /emoji/<cp>.json — коли ми ЗАМІНЮЄМО емодзі (напр. Noto →
// Telegram), браузер/Telegram-WebView віддавав старий із кешу. Додаємо ?v=build
// → кожен білд тягне свіжі, всередині білда кешується.
const _V = typeof __BUILD_ID__ !== "undefined" ? __BUILD_ID__ : "";
const _bust = (url: string) => (_V ? `${url}?v=${_V}` : url);

// Пуленепробивний бастинг проти агресивного кешу Telegram-WebView (owner
// 2026-07-23): коли ЗАМІНЮЄМО емодзі (Noto → Telegram), деякі WebView віддають
// старий файл із кешу навіть з no-store і навіть ігноруючи ?v=. Тому даємо
// заміненим емодзі НОВЕ ІМʼЯ ФАЙЛА — цей шлях WebView ще ніколи не тягнув, кешу
// для нього фізично нема. Замінив ще щось → бампни версію тут (.v3, ...).
const FILE_VER: Record<string, string> = {
  "1f382": "1f382.v2", // 🎂 торт — справжній Telegram
  "1f4bb": "1f4bb.v2", // 💻 ноутбук — справжній Telegram
};
const fileFor = (cp: string) => FILE_VER[cp] ?? cp;

// ВШИТІ лотті (owner 2026-07-23, фінальний фікс сага-про-торт): для замінених
// емодзі анімацію імпортуємо ЯК МОДУЛЬ БАНДЛА (vite → окремий чанк з
// content-hash іменем). Жодного fetch за стабільним URL → липкий fetch-кеш
// Telegram-WebView (який ігнорує no-store і ?v=) фізично не при чому: анімація
// приїжджає тим самим каналом, що й код. Якщо у юзера є нове UI — є і нова
// анімація. Корінь саги: старий канал fetch("/emoji/<cp>.json") залипав на
// старому файлі назавжди.
const LOCAL_LOTTIE = import.meta.glob("../emoji-lottie/*.json") as Record<
  string,
  () => Promise<{ default: unknown }>
>;
const localLottieFor = (cp: string) => LOCAL_LOTTIE[`../emoji-lottie/${cp}.json`];

// Кеш завантажених Lottie-JSON (щоб повторний рендер був миттєвий).
const lottieCache = new Map<string, unknown>();

// Спільний лоадер lottie-web (один чанк на всіх). Прогріваємо ОДРАЗУ при
// завантаженні модуля (TgEmoji підключений у корені через AppAlert), щоб
// перша ж анімація грала без затримки (юзер 2026-06-15: «перший раз не
// анімувалось, після close/open — ок» — це була затримка завантаження чанку).
let lottiePromise: Promise<typeof import("lottie-web").default> | null = null;
function getLottie() {
  if (!lottiePromise) {
    lottiePromise = import("lottie-web").then((m) => m.default);
  }
  return lottiePromise;
}
//: Емодзі, які найчастіше стоять НАД ЗАГОЛОВКОМ ПОПАПА. Їхні анімації
//: прогріваємо після першого дотику до екрана: попап майже завжди
//: відкривається після якоїсь дії, і до того моменту файл уже в кеші, тому
//: анімація грає з першого кадру (owner 2026-09-23). Увесь набір прогрівати
//: не можна — це 7 МБ.
const POPUP_CPS = ["1f510", "23f3", "1f48e", "274c", "2705", "1f389"];

export function preloadTgEmojiData(cp: string): void {
  if (!LOTTIE_CPS.has(cp) || lottieCache.has(cp)) return;
  const local = localLottieFor(cp);
  const load = local
    ? local().then((mod) => (mod as { default?: unknown }).default ?? mod)
    : fetch(_bust(`/emoji/${fileFor(cp)}.json`)).then((r) =>
        r.ok ? r.json() : Promise.reject(new Error("no lottie")),
      );
  void load.then((data) => lottieCache.set(cp, data)).catch(() => {});
}

if (typeof window !== "undefined") {
  const warmPopups = () => {
    // По черзі, щоб не забити канал на старті.
    POPUP_CPS.reduce(
      (chain, cp) => chain.then(() => {
        preloadTgEmojiData(cp);
        return new Promise<void>((r) => setTimeout(r, 300));
      }),
      Promise.resolve(),
    );
  };
  window.addEventListener("pointerdown", warmPopups, { once: true, passive: true });

  const warm = () => {
    void getLottie();
    // Прогріваємо локальні PNG-плейсхолдери у кеш браузера, щоб при
    // відкритті попапа емодзі було ОДРАЗУ (без білої порожнечі).
    for (const cp of LOTTIE_CPS) {
      const img = new Image();
      img.src = _bust(PNG_BASE + fileFor(cp) + ".png");
    }
  };
  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  if (ric) ric(warm);
  else setTimeout(warm, 1000);
}

function toCodepoints(ch: string): string {
  const cps: string[] = [];
  for (const sym of ch) {
    const c = sym.codePointAt(0);
    if (c === undefined || c === 0xfe0f) continue;
    cps.push(c.toString(16));
  }
  return cps.join("-");
}

/** Розмір емодзі задають числом «в дизайнерських px» (база 390px ширини).
 *  Рендеримо в rem, щоб емодзі масштабувались разом з усім інтерфейсом
 *  (owner 2026-08-12: «щоб на більшому і меншому телефоні було так само»). */
const _rem = (n: number) => `${n / 16}rem`;

export function TgEmoji({
  char,
  size = 24,
  className,
  persistBase = false,
  still = false,
}: {
  char: string;
  size?: number;
  className?: string;
  /** Статична картинка без анімації — для емодзі-маркерів у списках, де
   *  десяток одночасних анімацій лише рябить (owner 2026-09-23, попап шансів). */
  still?: boolean;
  /** Не ховати статичний PNG під Lottie. Для емодзі, чия анімація має
   *  «порожні» кадри (напр. 💬) — інакше на тих кадрах емодзі зникає. */
  persistBase?: boolean;
}) {
  const rawCp = toCodepoints(char);
  // Підміняємо на анімований аналог (якщо є), інакше лишаємо оригінал.
  const cp = ALIAS[rawCp] ?? rawCp;
  const hasLottie = !still && LOTTIE_CPS.has(cp);
  const boxRef = useRef<HTMLSpanElement>(null);
  const [lottieReady, setLottieReady] = useState(false);
  const [pngFailed, setPngFailed] = useState(false);

  useEffect(() => {
    if (!hasLottie) return;
    let cancelled = false;
    let anim: import("lottie-web").AnimationItem | null = null;
    (async () => {
      try {
        let data = lottieCache.get(cp);
        if (!data) {
          const local = localLottieFor(cp);
          if (local) {
            // Вшитий у бандл чанк (content-hash) — липкий кеш не при чому.
            const mod = await local();
            data = (mod as { default?: unknown }).default ?? mod;
          } else {
            // cache:"no-store" — Telegram-WebView для fetch() ігнорує серверний
            // no-store і віддає старий закешований Lottie. Форсуємо мережу.
            const r = await fetch(_bust(`/emoji/${fileFor(cp)}.json`), {
              cache: "no-store",
            });
            if (!r.ok) throw new Error("no lottie");
            data = await r.json();
          }
          lottieCache.set(cp, data);
        }
        if (cancelled || !boxRef.current) return;
        const lottie = await getLottie();
        if (cancelled || !boxRef.current) return;
        anim = lottie.loadAnimation({
          container: boxRef.current,
          renderer: "svg",
          loop: true,
          autoplay: true,
          // Окрема копія на кожен екземпляр: lottie-web дописує в дані свої
          // службові поля, і другий екземпляр того самого емодзі на спільному
          // обʼєкті міг лишитись нерухомим (owner 2026-09-23: 💎 у попапі
          // шансів стояв статичним, коли той самий 💎 уже був на сторінці).
          animationData:
            typeof structuredClone === "function"
              ? structuredClone(data)
              : JSON.parse(JSON.stringify(data)),
        });
        // Ховаємо PNG лише коли Lottie РЕАЛЬНО намалював перший кадр
        // (DOMLoaded), інакше була мить порожнечі (юзер 2026-06-15).
        const reveal = () => {
          if (!cancelled) setLottieReady(true);
        };
        anim.addEventListener("DOMLoaded", reveal);
        // Фолбек: якщо подія не спрацює — показати за 400мс.
        setTimeout(reveal, 400);
      } catch {
        /* лишаємо статичний PNG */
      }
    })();
    return () => {
      cancelled = true;
      if (anim) anim.destroy();
    };
  }, [cp, hasLottie]);

  // Нативний символ — ЛИШЕ якщо немає ні PNG, ні Lottie.
  // ВАЖЛИВО (юзер 2026-06-27): раніше тут був ранній return при
  // `pngFailed && !lottieReady`, який ДЕМОНТУВАВ Lottie-контейнер (boxRef)
  // ще до того як анімація відмалювалась → для емодзі БЕЗ PNG (нові 🥇/❌)
  // Lottie чіплявся до від'єднаного вузла й не анімувався. Тепер контейнер
  // boxRef рендериться ЗАВЖДИ коли є Lottie, а нативний символ — лише
  // плейсхолдер-шар поки Lottie готується.
  if (pngFailed && !hasLottie) {
    return (
      <span className={className} style={{ fontSize: _rem(size), lineHeight: 1 }}>
        {char}
      </span>
    );
  }

  return (
    <span
      className={className}
      style={{
        position: "relative",
        display: "inline-block",
        width: _rem(size),
        height: _rem(size),
        verticalAlign: "middle",
      }}
    >
      {/* Статичний Apple-PNG — миттєво; ховаємо коли Lottie готовий */}
      {!pngFailed && (
        <img
          src={_bust(PNG_BASE + fileFor(cp) + ".png")}
          alt={char}
          width={size}
          height={size}
          draggable={false}
          onError={() => setPngFailed(true)}
          style={{
            position: "absolute",
            inset: 0,
            width: _rem(size),
            height: _rem(size),
            objectFit: "contain",
            opacity: persistBase ? 1 : lottieReady ? 0 : 1,
            // Owner 2026-09-23: «попап відкривається зі статичним емодзі,
            // потім ривок — і анімований». Ривок давала МИТТЄВА підміна двох
            // шарів. Тепер вони перетікають один в одний за 160 мс.
            transition: "opacity 160ms linear",
          }}
        />
      )}
      {/* Нативний символ як плейсхолдер коли PNG нема і Lottie ще не готовий */}
      {pngFailed && !lottieReady && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: _rem(size),
            lineHeight: 1,
          }}
        >
          {char}
        </span>
      )}
      {/* Lottie-оверлей (60fps) — контейнер ЗАВЖДИ змонтований коли є Lottie */}
      {hasLottie && (
        <span
          ref={boxRef}
          style={{
            position: "absolute",
            inset: 0,
            width: _rem(size),
            height: _rem(size),
            display: "block",
            opacity: lottieReady ? 1 : 0,
            transition: "opacity 160ms linear",
          }}
        />
      )}
    </span>
  );
}
